import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  TextField,
  IconButton,
  MenuItem,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  FormHelperText,
  Divider,
  FormGroup,
  FormControlLabel,
  Radio,
  RadioGroup
} from '@mui/material';
import {
  X,
  Trash2,
  Plus,
  Save,
  DollarSign,
  Calendar,
  Building,
  CreditCard,
  Users,
  Package,
  Hash,
  ShoppingCart
} from 'lucide-react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import api from '../services/api';

const AddSaleModal = ({ open, onClose, onSaleAdded }) => {
  // State for form data
  const [formData, setFormData] = useState({
    employee_id: '',
    hotel_id: '',
    payment_method_id: '',
    sale_date: new Date(),
    tip: 0
  });

  // Validation errors
  const [errors, setErrors] = useState({
    employee_id: false,
    hotel_id: false,
    payment_method_id: false,
    sale_date: false,
    tip: false,
    items: false
  });

  // Sale items
  const [items, setItems] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [serviceError, setServiceError] = useState(false);
  const [quantityError, setQuantityError] = useState(false);

  // Data from API
  const [services, setServices] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  // NEW: Active employees (grouped + searchable)
  const [activeEmployees, setActiveEmployees] = useState([]); // raw rows from /employees
  const [empSearch, setEmpSearch] = useState('');

  // UI control
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Load data when the modal opens
  useEffect(() => {
    if (!open) return;

    setLoading(true);
    resetForm();

    const fetchData = async () => {
      try {
        const [servicesRes, hotelsRes, employeesRes, paymentMethodsRes] = await Promise.all([
          api.get('/services'),
          api.get('/hotels'),
          api.get('/employees'),
          api.get('/payment_methods')
        ]);

        const activeServices = (servicesRes.data || []).filter((s) => s.status === 'active');
        setServices(activeServices);
        setHotels(hotelsRes.data || []);

        // Keep only active employees, sort A–Z by name
        const active = (employeesRes.data || []).filter((emp) => emp[6] === 'active');
        const sorted = [...active].sort((a, b) => String(a[1]).localeCompare(String(b[1]), undefined, { sensitivity: 'base' }));
        setActiveEmployees(sorted);

        setPaymentMethods(paymentMethodsRes.data || []);

        // Reset employee search and selection
        setEmpSearch('');
        setFormData((prev) => ({ ...prev, employee_id: '' }));
      } catch (err) {
        console.error('❌ Error loading data:', err);
        showSnackbar('Error loading necessary data', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open]);

  const resetForm = () => {
    setFormData({ employee_id: '', hotel_id: '', payment_method_id: '', sale_date: new Date(), tip: 0 });
    setItems([]);
    setSelectedService('');
    setQuantity(1);
    setErrors({ employee_id: false, hotel_id: false, payment_method_id: false, sale_date: false, tip: false, items: false });
    setServiceError(false);
    setQuantityError(false);
  };

  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: false }));
  };

  const validateItemFields = () => {
    let isValid = true;
    if (!selectedService) { setServiceError(true); isValid = false; } else setServiceError(false);
    if (!quantity || quantity <= 0) { setQuantityError(true); isValid = false; } else setQuantityError(false);
    return isValid;
  };

  const handleAddItem = () => {
    if (!validateItemFields()) { showSnackbar('Please fill in the service fields correctly', 'warning'); return; }
    const service = services.find((s) => s.id === parseInt(selectedService));
    if (!service) { showSnackbar('Service not found', 'error'); return; }
    setItems((prev) => ([ ...prev, { service_id: parseInt(service.id), name: service.name, price: parseFloat(service.price), quantity: parseFloat(quantity) } ]));
    if (errors.items) setErrors((prev) => ({ ...prev, items: false }));
    showSnackbar('Item added successfully');
    setSelectedService(''); setQuantity(1);
  };

  const handleDeleteItem = (index) => {
    const updated = [...items]; updated.splice(index, 1); setItems(updated);
    if (updated.length === 0) setErrors((prev) => ({ ...prev, items: true }));
    showSnackbar('Item removed');
  };

  const validateForm = () => {
    const newErrors = {
      employee_id: !formData.employee_id,
      hotel_id: !formData.hotel_id,
      payment_method_id: !formData.payment_method_id,
      sale_date: !formData.sale_date,
      tip: formData.tip < 0,
      items: items.length === 0
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSave = async () => {
    if (!validateForm()) { showSnackbar('Please fill in all required fields', 'warning'); return; }
    setLoading(true);
    try {
      const formattedDate = format(formData.sale_date, 'yyyy-MM-dd');
      const saleData = {
        employee_id: parseInt(formData.employee_id),
        hotel_id: parseInt(formData.hotel_id),
        payment_method_id: parseInt(formData.payment_method_id),
        sale_date: formattedDate,
        tip: parseFloat(formData.tip) || 0,
        items: items.map((item) => ({ service_id: item.service_id, quantity: item.quantity }))
      };
      await api.post('/sales', saleData);
      showSnackbar('Sale created successfully!');
      setTimeout(() => { onSaleAdded && onSaleAdded(); onClose && onClose(); }, 800);
    } catch (err) {
      console.error('Error creating sale:', err);
      showSnackbar(`Error creating sale: ${err.response?.data?.msg || err.message}`, 'error');
      setLoading(false);
    }
  };

  const calculateItemsSubtotal = () => items.reduce((total, item) => total + (parseFloat(item.price) * parseFloat(item.quantity)), 0);
  const calculateTotal = () => {
    const itemsTotal = calculateItemsSubtotal();
    const tipAmount = parseFloat(formData.tip) || 0;
    return (itemsTotal + tipAmount).toFixed(2);
  };

  // Group + filter employees (active only)
  const filteredAndGroupedActive = useMemo(() => {
    const filtered = (activeEmployees || [])
      .filter((e) => {
        const name = String(e[1] || '');
        if (!empSearch.trim()) return true;
        return name.toLowerCase().includes(empSearch.trim().toLowerCase());
      })
      .sort((a, b) => String(a[1]).localeCompare(String(b[1]), undefined, { sensitivity: 'base' }));

    const groups = {};
    filtered.forEach((emp) => {
      const name = String(emp[1] || '');
      const letter = name.charAt(0).toUpperCase();
      const key = /[A-Z]/.test(letter) ? letter : '#';
      if (!groups[key]) groups[key] = [];
      groups[key].push(emp);
    });

    const orderedKeys = Object.keys(groups).sort((a, b) => {
      if (a === '#') return 1;
      if (b === '#') return -1;
      return a.localeCompare(b);
    });

    return { groups, orderedKeys };
  }, [activeEmployees, empSearch]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={(e, reason) => { if (reason !== 'backdropClick' && !loading) { onClose && onClose(); } }}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCart size={24} color="#1976d2" />
            <span>New Sale</span>
          </Box>
          {loading && <CircularProgress size={24} />}
        </DialogTitle>

        <DialogContent dividers>
          {/* Sale Information */}
          <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ color: '#333' }}>
            Sale Information
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {/* DATE */}
            <DatePicker
              label="Sale Date *"
              value={formData.sale_date}
              onChange={(newDate) => handleFormChange('sale_date', newDate)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  disabled: loading,
                  error: errors.sale_date,
                  helperText: errors.sale_date ? 'Date is required' : '',
                  InputProps: { startAdornment: (<InputAdornment position="start"><Calendar size={18} color="#666"/></InputAdornment>) }
                }
              }}
            />

            {/* EMPLOYEE PICKER — active only, grouped, searchable */}
            <Box sx={{ border: '1px solid #eee', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 1.5, position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}>
                <TextField
                  value={empSearch}
                  onChange={(e) => setEmpSearch(e.target.value)}
                  placeholder="Search employee..."
                  fullWidth
                  size="small"
                  InputProps={{ startAdornment: (<InputAdornment position="start"><Users size={16} /></InputAdornment>) }}
                />
              </Box>
              <Divider />
              <Box sx={{ maxHeight: 300, overflow: 'auto', p: 1.5 }}>
                <RadioGroup
                  name="employee_select"
                  value={String(formData.employee_id || '')}
                  onChange={(e) => handleFormChange('employee_id', e.target.value)}
                >
                  {filteredAndGroupedActive.orderedKeys.map((letter) => (
                    <Box key={letter} sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ px: 1.5, py: 0.5, mb: 1, fontWeight: 700, bgcolor: 'action.hover', borderRadius: 1 }}>
                        {letter}
                      </Typography>
                      <FormGroup>
                        {filteredAndGroupedActive.groups[letter].map((emp) => (
                          <FormControlLabel
                            key={emp[0]}
                            value={String(emp[0])}
                            control={<Radio />}
                            label={<Box display="flex" alignItems="center" gap={1}><Users size={16} /><Typography sx={{ fontWeight: 500 }}>{emp[1]}</Typography></Box>}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  ))}
                  {filteredAndGroupedActive.orderedKeys.length === 0 && (
                    <Typography sx={{ color: 'text.secondary' }}>No employees found.</Typography>
                  )}
                </RadioGroup>
              </Box>
            </Box>
            {errors.employee_id && <FormHelperText error>Employee is required</FormHelperText>}

            {/* HOTEL */}
            <FormControl fullWidth error={errors.hotel_id}>
              <InputLabel id="hotel-label">Hotel *</InputLabel>
              <Select
                labelId="hotel-label"
                value={formData.hotel_id}
                onChange={(e) => handleFormChange('hotel_id', e.target.value)}
                label="Hotel *"
                disabled={loading}
                startAdornment={<InputAdornment position="start"><Building size={18} color="#666" /></InputAdornment>}
              >
                {hotels.map((hotel) => (<MenuItem key={hotel.id} value={hotel.id}>{hotel.name}</MenuItem>))}
              </Select>
              {errors.hotel_id && <FormHelperText>Hotel is required</FormHelperText>}
            </FormControl>

            {/* PAYMENT METHOD */}
            <FormControl fullWidth error={errors.payment_method_id}>
              <InputLabel id="payment-method-label">Payment Method *</InputLabel>
              <Select
                labelId="payment-method-label"
                value={formData.payment_method_id}
                onChange={(e) => handleFormChange('payment_method_id', e.target.value)}
                label="Payment Method *"
                disabled={loading}
                startAdornment={<InputAdornment position="start"><CreditCard size={18} color="#666" /></InputAdornment>}
              >
                {paymentMethods.map((method) => (<MenuItem key={method.id} value={method.id}>{method.method}</MenuItem>))}
              </Select>
              {errors.payment_method_id && <FormHelperText>Payment method is required</FormHelperText>}
            </FormControl>

            {/* TIP */}
            <TextField
              label="Tip"
              type="number"
              fullWidth
              value={formData.tip}
              onChange={(e) => handleFormChange('tip', e.target.value)}
              disabled={loading}
              error={errors.tip}
              helperText={errors.tip ? 'Invalid value' : ''}
              InputProps={{ startAdornment: (<InputAdornment position="start"><DollarSign size={18} color="#666" /></InputAdornment>) }}
            />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Add Items Section */}
          <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ color: '#333' }}>
            Add Items
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <FormControl fullWidth error={serviceError}>
              <InputLabel id="service-label">Service *</InputLabel>
              <Select
                labelId="service-label"
                value={selectedService}
                onChange={(e) => { setSelectedService(e.target.value); setServiceError(false); }}
                label="Service *"
                disabled={loading}
                startAdornment={<InputAdornment position="start"><Package size={18} color="#666" /></InputAdornment>}
              >
                {services.map((s) => (<MenuItem key={s.id} value={s.id}>{s.name} - ${parseFloat(s.price).toFixed(2)}</MenuItem>))}
              </Select>
              {serviceError && <FormHelperText>Select a service</FormHelperText>}
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                label="Quantity *"
                type="number"
                value={quantity}
                onChange={(e) => { setQuantity(parseFloat(e.target.value)); setQuantityError(false); }}
                inputProps={{ min: 0.1, step: 0.1 }}
                fullWidth
                disabled={loading}
                error={quantityError}
                helperText={quantityError ? 'Invalid quantity' : ''}
                InputProps={{ startAdornment: (<InputAdornment position="start"><Hash size={18} color="#666" /></InputAdornment>) }}
              />

              <IconButton
                onClick={handleAddItem}
                disabled={loading}
                sx={{ background: 'linear-gradient(to right, #4caf50, #388e3c)', color: 'white', '&:hover': { background: 'linear-gradient(to right, #45a049, #2e7d32)' }, '&:disabled': { background: '#e0e0e0', color: '#999' }, minWidth: 48, height: 48 }}
              >
                <Plus size={20} />
              </IconButton>
            </Box>
          </Box>

          {/* Items List */}
          <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ color: '#333' }}>
            Sale Items {errors.items && (<Typography variant="caption" color="error" sx={{ ml: 1 }}>(Add at least one item)</Typography>)}
          </Typography>

          {items.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, bgcolor: errors.items ? 'rgba(211, 47, 47, 0.04)' : '#f9f9f9', borderRadius: 2, border: errors.items ? '1px dashed #d32f2f' : '1px dashed #ddd', mb: 2 }}>
              <Typography color={errors.items ? 'error' : 'text.secondary'}>No items added. Add items to the sale.</Typography>
            </Box>
          ) : (
            <>
              <List sx={{ bgcolor: '#f9f9f9', borderRadius: 2, mb: 2, maxHeight: '300px', overflow: 'auto' }}>
                {items.map((item, idx) => (
                  <ListItem key={idx} secondaryAction={
                      <IconButton size="small" onClick={() => handleDeleteItem(idx)} disabled={loading} sx={{ color: '#f44336', '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.1)' } }}>
                        <Trash2 size={16} />
                      </IconButton>
                    } sx={{ bgcolor: 'white', mb: 1, borderRadius: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}
                  >
                    <ListItemText primary={item.name} secondary={`Quantity: ${item.quantity} × $${parseFloat(item.price).toFixed(2)} = $${(item.quantity * item.price).toFixed(2)}`} />
                  </ListItem>
                ))}
              </List>

              {/* Sale Summary */}
              <Box sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ color: '#666' }}>Subtotal:</Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>${calculateItemsSubtotal().toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#666' }}>Tip:</Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>${(parseFloat(formData.tip) || 0).toFixed(2)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#333' }}>Total:</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: '#333' }}>${calculateTotal()}</Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>

        {/* Action Buttons */}
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button onClick={onClose} disabled={loading} startIcon={<X size={18} />} fullWidth sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', color: '#333', border: '1px solid #ddd', '&:hover': { backgroundColor: '#e0e0e0' } }}>
            Cancel
          </Button>

          <Button onClick={handleSave} variant="contained" startIcon={<Save size={18} />} disabled={loading} fullWidth sx={{ fontWeight: 'bold', background: 'linear-gradient(to right, #1976d2, #0d47a1)', color: '#fff', '&:hover': { background: 'linear-gradient(to right, #1565c0, #003c8f)' } }}>
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Sale'}
          </Button>
        </Box>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
};

export default AddSaleModal;
